import { APPROVAL_MESSAGES, type ApprovalDetail, type ApprovalListItem } from '@fos/shared/contracts/approvals';
import {
  APPROVAL_DISPLAY_STATUS_LABELS,
  APPROVAL_DISPLAY_STATUS_TONES,
  APPROVAL_PRIMARY_ACTION_LABELS,
} from '@fos/shared/labels';
import { Check, ChevronDown, ChevronRight, FlaskConical, Info, Send, TriangleAlert } from 'lucide-react';
import { type ReactNode, type Ref, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { Spinner } from '../../../ui/Spinner';
import { ErrorState, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { approvalMeta, approvalTime, isReviewKind, PREVIEW_KIND_ICONS, simulationNote } from '../approval-format';
import { isInFlight, useApprovalDetail, useApprovalsRefresh } from '../hooks';
import { EditApprovalDialog, editFormKind, RejectApprovalDialog } from './ApprovalDialogs';
import { ApprovalPreviewView } from './ApprovalPreview';

/** Ikona typu na kafelku accent/subtle (DESIGN §5 „Akceptacja”). */
export function ApprovalTypeIcon({ kind, className }: { kind: ApprovalListItem['previewKind']; className?: string }) {
  const Icon = PREVIEW_KIND_ICONS[kind];
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-subtle text-accent',
        className,
      )}
    >
      <Icon size={18} strokeWidth={1.75} />
    </span>
  );
}

/** Status po decyzji (Wykonywana / Wykonana / Błąd / Odrzucona / Wycofana / Wygasła). */
export function ApprovalStatusLine({ item }: { item: ApprovalListItem | ApprovalDetail }) {
  if (item.status === 'approved' || item.status === 'executing') {
    return (
      <span role="status" className="inline-flex items-center gap-2 text-caption text-fg-secondary">
        <Spinner size={14} />
        {item.status === 'approved' ? 'Zatwierdzona, za chwilę się wykona…' : 'Wykonywana…'}
      </span>
    );
  }
  return (
    <StatusPill tone={APPROVAL_DISPLAY_STATUS_TONES[item.displayStatus]}>
      {APPROVAL_DISPLAY_STATUS_LABELS[item.displayStatus]}
      {item.simulated && item.status === 'succeeded' ? ' · symulacja' : ''}
    </StatusPill>
  );
}

function Outcome({ detail, withStatus }: { detail: ApprovalDetail; withStatus: boolean }) {
  const decided = detail.decidedBy
    ? `${detail.decidedBy.name} · ${approvalTime(detail.decidedAt ?? detail.createdAt)}`
    : null;
  if (detail.status === 'succeeded') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {withStatus && <ApprovalStatusLine item={detail} />}
          {detail.executedAt && (
            <span className="text-caption text-fg-muted">
              {decided ? `Decyzja: ${decided} · ` : ''}wykonano {approvalTime(detail.executedAt)}
            </span>
          )}
        </div>
        {detail.simulated && (
          <ContextBanner tone="info" icon={FlaskConical}>
            {simulationNote(detail.previewKind, 'done')}
          </ContextBanner>
        )}
      </div>
    );
  }
  if (detail.status === 'failed') {
    return (
      <div className="flex flex-col gap-2">
        <FormMessage>
          {detail.error ?? APPROVAL_MESSAGES.executionFailed}
          {detail.manualCheckRequired && (
            <span className="block text-fg-secondary">
              Sprawdź u dostawcy, czy operacja się wykonała, zanim przygotujesz ją ponownie.
            </span>
          )}
        </FormMessage>
        {decided && <span className="text-caption text-fg-muted">Decyzja: {decided}</span>}
      </div>
    );
  }
  if (isInFlight(detail)) return withStatus ? <ApprovalStatusLine item={detail} /> : null;
  const reason =
    detail.displayStatus === 'withdrawn'
      ? detail.withdrawnReason
      : detail.displayStatus === 'rejected'
        ? detail.decisionNote
        : null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {withStatus && <ApprovalStatusLine item={detail} />}
      {(decided || reason) && (
        <span className="text-caption text-fg-muted">{[decided, reason].filter(Boolean).join(' · ')}</span>
      )}
    </div>
  );
}

/**
 * Jawne potwierdzenia przed zatwierdzeniem (Fala 6): np. odbiorca maila spoza kontaktów klienta wskazany przez agenta.
 * Ostrzeżenie z powodem i pole „Potwierdzam odbiorcę: …” dla każdego; bez zaznaczenia wszystkich „Zatwierdź” jest
 * nieaktywne (serwer i tak odrzuci zatwierdzenie bez potwierdzeń).
 */
function ConfirmationsBlock({
  items,
  checked,
  onToggle,
  disabled,
  groupRef,
}: {
  items: ApprovalDetail['confirmations'];
  checked: ReadonlySet<string>;
  onToggle: (key: string, on: boolean) => void;
  disabled: boolean;
  groupRef?: Ref<HTMLFieldSetElement>;
}) {
  return (
    <fieldset ref={groupRef} className="flex min-w-0 flex-col gap-2">
      <legend className="sr-only">Potwierdzenia przed zatwierdzeniem</legend>
      <ContextBanner tone="warning" icon={TriangleAlert}>
        <span className="flex flex-col gap-0.5">
          {items.map((c) => (
            <span key={c.key}>
              <span className="font-medium">{c.key}</span> — {c.reason}
            </span>
          ))}
        </span>
      </ContextBanner>
      {items.map((c) => (
        <Checkbox
          key={c.key}
          checked={checked.has(c.key)}
          onCheckedChange={(on) => onToggle(c.key, on)}
          label={c.label}
          disabled={disabled}
          touchTarget
        />
      ))}
    </fieldset>
  );
}

/**
 * Rozwinięta treść karty (Figma 03 desktop i ekran szczegółów): banery (symulacja, nieaktualne dane, ukryte kwoty),
 * pełny podgląd, jedna linia „co się stanie” z informacją o nieodwracalności, potwierdzenia wymagane przez akcję
 * (Fala 6: odbiorca wskazany przez agenta), akcje Zatwierdź i wykonaj (Primary) / Edytuj / Odrzuć, a po decyzji —
 * stan wykonania odświeżany do końca (Wykonywana → Wykonana / Błąd).
 */
export function ApprovalExpanded({
  detail,
  focusPrimary = false,
  onDecided,
  layout = 'card',
  showFacts = true,
  showPreview = true,
  showDescription = true,
}: {
  detail: ApprovalDetail;
  focusPrimary?: boolean;
  onDecided?: (id: string) => void;
  /** `page`: ekran szczegółów — na mobile duży przycisk na całą szerokość (Figma 03 mobile, wdrożenie) */
  layout?: 'card' | 'page';
  showFacts?: boolean;
  showPreview?: boolean;
  /** opis w podglądzie wdrożenia (ekran szczegółów pokazuje go pod nagłówkiem) */
  showDescription?: boolean;
}) {
  const refresh = useApprovalsRefresh();
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const confirmationsRef = useRef<HTMLFieldSetElement>(null);
  const pending = detail.status === 'pending' && detail.displayStatus === 'pending';
  const confirmations = pending ? (detail.confirmations ?? []) : [];
  const [confirmedKeys, setConfirmedKeys] = useState<{ hash: string; keys: ReadonlySet<string> }>({
    hash: detail.payloadHash,
    keys: new Set(),
  });
  // nowa rewizja treści (edycja) = potwierdzenia od nowa
  const checkedKeys = confirmedKeys.hash === detail.payloadHash ? confirmedKeys.keys : new Set<string>();
  const toggleConfirmation = (key: string, on: boolean) => {
    const next = new Set(checkedKeys);
    if (on) next.add(key);
    else next.delete(key);
    setConfirmedKeys({ hash: detail.payloadHash, keys: next });
  };
  const allConfirmed = confirmations.every((c) => checkedKeys.has(c.key));

  const needsConfirmation = confirmations.length > 0;
  useEffect(() => {
    if (!focusPrimary || !pending || !detail.canDecide) return;
    // z potwierdzeniami „Zatwierdź” jest nieaktywne do ich zaznaczenia — fokus na pierwsze pole potwierdzenia
    if (needsConfirmation) confirmationsRef.current?.querySelector<HTMLElement>('[role="checkbox"]')?.focus();
    else primaryRef.current?.focus();
  }, [focusPrimary, pending, detail.canDecide, needsConfirmation]);

  const approve = trpc.approvals.approve.useMutation({
    onSuccess: async (next) => {
      utils.approvals.get.setData({ id: next.id }, next);
      toast.success('Zatwierdzono', { description: 'Akcja się wykonuje — wynik zobaczysz w Historii.' });
      onDecided?.(next.id);
      await refresh();
    },
    onError: async (err) => {
      toast.error(errorMessage(err, 'Nie udało się zatwierdzić akcji.'));
      if (appErrorCode(err) === 'CONFLICT') await refresh();
    },
  });

  const canEdit = pending && detail.canEdit && editFormKind(detail) !== null;
  const canReject =
    pending &&
    (detail.canDecide || detail.contentHidden || detail.decideBlockedReason === APPROVAL_MESSAGES.targetChanged);
  const PrimaryIcon = detail.previewKind === 'email' ? Send : Check;

  return (
    <div className="flex flex-col gap-3">
      {pending && detail.willSimulate && (
        <ContextBanner tone="info" icon={FlaskConical}>
          {simulationNote(detail.previewKind, 'ahead')}
        </ContextBanner>
      )}
      {pending && detail.targetStale && (
        <ContextBanner tone="warning">
          {APPROVAL_MESSAGES.targetChanged}. Odrzuć tę akcję i przygotuj ją jeszcze raz.
        </ContextBanner>
      )}
      {showPreview && (
        <ApprovalPreviewView preview={detail.preview} showFacts={showFacts} showDescription={showDescription} />
      )}
      {detail.effects && pending && layout === 'card' && (
        <p className="flex items-start gap-2 text-caption text-fg-muted">
          <Info size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
          <span>{detail.effects.text}</span>
        </p>
      )}
      {!pending && <Outcome detail={detail} withStatus={layout === 'page'} />}
      {pending && !detail.canDecide && detail.decideBlockedReason && !detail.targetStale && (
        <p className="text-caption text-fg-muted">{detail.decideBlockedReason}</p>
      )}
      {confirmations.length > 0 && (
        <ConfirmationsBlock
          items={confirmations}
          checked={checkedKeys}
          onToggle={toggleConfirmation}
          disabled={!detail.canDecide || approve.isPending || approve.isSuccess}
          groupRef={confirmationsRef}
        />
      )}
      {pending && (detail.canDecide || canEdit || canReject) && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-2',
            layout === 'page' && 'max-md:mt-4 max-md:flex-col max-md:items-stretch',
          )}
        >
          {detail.canDecide && (
            <Button
              ref={primaryRef}
              variant="primary"
              icon={PrimaryIcon}
              className={layout === 'page' ? 'max-md:h-10 max-md:w-full max-md:px-4' : undefined}
              loading={approve.isPending}
              disabled={approve.isSuccess || !allConfirmed}
              onClick={() =>
                approve.mutate({
                  id: detail.id,
                  payloadHash: detail.payloadHash,
                  ...(confirmations.length > 0 ? { confirmations: confirmations.map((c) => c.key) } : {}),
                })
              }
            >
              {/* Figma 03 mobile (ekran wdrożenia): krótkie „Wdróż” */}
              {layout === 'page' && detail.previewKind === 'deploy'
                ? 'Wdróż'
                : APPROVAL_PRIMARY_ACTION_LABELS[detail.previewKind]}
            </Button>
          )}
          <div className={cn('flex flex-wrap items-center gap-2', layout === 'page' && 'max-md:justify-center')}>
            {canEdit && (
              <Button
                variant="secondary"
                className={layout === 'page' ? 'max-md:h-11' : undefined}
                onClick={() => setEditing(true)}
                disabled={approve.isPending}
              >
                Edytuj
              </Button>
            )}
            {canReject && (
              <Button
                variant="ghost"
                className={layout === 'page' ? 'max-md:h-11' : undefined}
                onClick={() => setRejecting(true)}
                disabled={approve.isPending}
              >
                Odrzuć
              </Button>
            )}
          </div>
        </div>
      )}
      {canEdit && <EditApprovalDialog detail={detail} open={editing} onOpenChange={setEditing} />}
      <RejectApprovalDialog
        approval={detail}
        open={rejecting}
        onOpenChange={setRejecting}
        onRejected={() => onDecided?.(detail.id)}
      />
    </div>
  );
}

/**
 * Karta akceptacji na liście (Figma 03). Desktop: zwinięta — ikona typu, tytuł, meta „typ · klient · przygotował X ·
 * godzina”, po prawej Odrzuć, Zatwierdź / Przejrzyj i chevron; rozwinięta — `ApprovalExpanded`. „Zatwierdź” na zwiniętej
 * karcie rozwija ją i ustawia fokus na „Zatwierdź i wykonaj” (najpierw pełny podgląd, potem zgoda). Mobile (Figma 03
 * mobile): tytuł, meta, początek treści i przyciski Odrzuć / Zatwierdź na całą szerokość; zatwierdzenie na ekranie
 * szczegółów (`/akceptacje/:id`).
 */
export function ApprovalCard({
  item,
  expanded,
  onToggle,
  emphasis = false,
  track = false,
  onDecided,
}: {
  item: ApprovalListItem;
  expanded: boolean;
  onToggle: (opts?: { focusPrimary?: boolean }) => void;
  /** śledź stan na żywo (karta po decyzji w tej wizycie, także zwinięta) */
  track?: boolean;
  /** pierwsza karta na mobile: przycisk Zatwierdź jako Primary (Figma 03 mobile) */
  emphasis?: boolean;
  onDecided?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const contentId = useId();
  const [focusPrimary, setFocusPrimary] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const detail = useApprovalDetail(item.id, expanded || track || isInFlight(item));
  const live: ApprovalListItem = detail.data ?? item;
  const pending = live.status === 'pending' && live.displayStatus === 'pending';
  const review = isReviewKind(live.previewKind);
  const detailPath = `/akceptacje/${live.id}`;

  const openForApproval = () => {
    setFocusPrimary(true);
    if (!expanded) onToggle({ focusPrimary: true });
  };

  let body: ReactNode = null;
  if (expanded) {
    if (detail.isPending) {
      body = (
        <div role="status" aria-label="Wczytywanie podglądu" className="flex flex-col gap-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      );
    } else if (detail.isError) {
      body = (
        <ErrorState
          compact
          title="Nie udało się wczytać podglądu"
          onRetry={() => void detail.refetch()}
          retrying={detail.isFetching}
        />
      );
    } else if (detail.data) {
      body = <ApprovalExpanded detail={detail.data} focusPrimary={focusPrimary} onDecided={onDecided} />;
    }
  }

  return (
    <article
      aria-label={live.summary}
      className={cn(
        // karta akceptacji = biały blok na szarym tle aplikacji (jak ui/Section)
        'rounded-xl border border-line bg-panel',
        emphasis && 'max-md:border-accent max-md:shadow-[0_0_0_1px_var(--accent-primary)]',
      )}
    >
      {/* desktop */}
      <div className="hidden items-center gap-3 px-4 py-3.5 md:flex">
        <ApprovalTypeIcon kind={live.previewKind} />
        <button
          type="button"
          onClick={() => onToggle()}
          aria-expanded={expanded}
          aria-controls={contentId}
          className="min-w-0 flex-1 rounded-md text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <span className="block truncate text-body-s font-semibold text-fg">{live.summary}</span>
          <span className="block truncate text-caption text-fg-muted">{approvalMeta(live)}</span>
        </button>
        {!expanded && pending && (live.canDecide || review) && (
          <div className="flex shrink-0 items-center gap-1.5">
            {live.canDecide && (
              <Button variant="ghost" size="s" onClick={() => setRejecting(true)}>
                Odrzuć
              </Button>
            )}
            {review ? (
              // propozycje zadań ze spotkania rozstrzyga się w przeglądzie spotkania (wybór i zmiany przed dodaniem)
              <Button
                variant="secondary"
                size="s"
                onClick={() => (live.reviewPath ? navigate(live.reviewPath) : onToggle())}
              >
                Przejrzyj
              </Button>
            ) : (
              <Button variant="secondary" size="s" onClick={openForApproval}>
                Zatwierdź
              </Button>
            )}
          </div>
        )}
        {!pending && <ApprovalStatusLine item={live} />}
        <button
          type="button"
          onClick={() => onToggle()}
          aria-label={expanded ? 'Zwiń' : 'Rozwiń'}
          aria-expanded={expanded}
          aria-controls={contentId}
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
        >
          {expanded ? (
            <ChevronDown size={18} strokeWidth={1.75} aria-hidden />
          ) : (
            <ChevronRight size={18} strokeWidth={1.75} aria-hidden />
          )}
        </button>
      </div>
      {expanded && (
        <div id={contentId} className="hidden px-4 pb-4 md:block">
          {body}
        </div>
      )}

      {/* mobile */}
      <div className="flex flex-col gap-3 p-4 md:hidden">
        <Link
          to={detailPath}
          className="flex items-start gap-3 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <ApprovalTypeIcon kind={live.previewKind} />
          <span className="min-w-0 flex-1">
            <span className="block text-body-s font-semibold text-fg">{live.summary}</span>
            <span className="block text-caption text-fg-muted">{approvalMeta(live, { compact: true })}</span>
          </span>
        </Link>
        {emphasis && live.excerpt && <p className="line-clamp-3 text-body-s text-fg-secondary">{live.excerpt}</p>}
        {pending && (live.canDecide || review) ? (
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setRejecting(true)}
              disabled={!live.canDecide}
              className="max-md:h-11"
            >
              Odrzuć
            </Button>
            <Button
              variant={emphasis ? 'primary' : 'secondary'}
              fullWidth
              onClick={() => navigate(review && live.reviewPath ? live.reviewPath : detailPath)}
              className="max-md:h-11"
            >
              {review ? 'Przejrzyj' : 'Zatwierdź'}
            </Button>
          </div>
        ) : (
          !pending && <ApprovalStatusLine item={live} />
        )}
      </div>
      <RejectApprovalDialog
        approval={live}
        open={rejecting}
        onOpenChange={setRejecting}
        onRejected={() => onDecided?.(live.id)}
      />
    </article>
  );
}
